import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUi } from '../i18n/uiTranslations';
import { useLanguage } from '../context/LanguageContext';
import { languages } from '../i18n/translations';
import { scrollToIdWhenReady, scrollToId } from '../lib/gsap';
import { SERVICE_LINKS } from './Header';
import { TELEGRAM_URL, TELEGRAM_PATH } from './TelegramButton';

const LOGO = 'https://customer-assets.emergentagent.com/job_pay-gaming/artifacts/9msxlx6e_logo_Logo%20Text%20-%20White.png';
const linkCls = 'text-[0.95rem] text-[#ABA59A] hover:text-[#F4F0E8] transition-colors duration-300';

const Social = ({ href, testId, label, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="w-11 h-11 rounded-full border border-[#F4F0E8]/12 flex items-center justify-center text-[#ABA59A] hover:text-[#080808] hover:bg-[#C8AA73] hover:border-[#C8AA73] transition-colors duration-300" data-testid={testId}>
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">{children}</svg>
  </a>
);

const Col = ({ title, children }) => (
  <div>
    <h4 className="font-mono-lux text-[0.7rem] tracking-[0.18em] uppercase text-[#C8AA73] mb-7">{title}</h4>
    <ul className="space-y-4">{children}</ul>
  </div>
);

export const Footer = () => {
  const { t, language, setLanguage } = useLanguage();
  const u = useUi();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      scrollToIdWhenReady(id);
    } else scrollToId(id);
  };

  return (
    <footer className="relative bg-[#060606] border-t border-[#F4F0E8]/[0.06] px-6 lg:px-12 pt-24 lg:pt-32 overflow-hidden" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }} data-testid="footer">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-14 lg:gap-10">
          <div className="sm:col-span-2 lg:col-span-4">
            <img src={LOGO} alt="SOFTMAYA" className="h-11 w-auto" />
            <p className="mt-8 font-display text-2xl text-[#F4F0E8] max-w-[320px] leading-snug">{t('footer.tagline')}</p>
            <div className="flex items-center gap-3 mt-10">
              <Social href="https://www.linkedin.com/company/softmaya" testId="social-linkedin" label="LinkedIn">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </Social>
              <Social href={TELEGRAM_URL} testId="social-telegram" label="Telegram">
                <path d={TELEGRAM_PATH} />
              </Social>
              <Social href="https://instagram.com/softmaya_official" testId="social-instagram" label="Instagram">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </Social>
            </div>
          </div>

          <div className="lg:col-span-3 lg:col-start-6">
            <Col title={t('footer.services')}>
              {SERVICE_LINKS.map((s) => (
                <li key={s.key}><Link to={s.link} className={linkCls} data-testid={`footer-service-${s.key}`}>{t(`services.${s.key}.title`)}</Link></li>
              ))}
            </Col>
          </div>

          <div className="lg:col-span-2">
            <Col title={t('footer.company')}>
              <li><button onClick={() => scrollToSection('about')} className={linkCls} data-testid="footer-about">{t('nav.about')}</button></li>
              <li><button onClick={() => scrollToSection('contact')} className={linkCls} data-testid="footer-contact">{t('nav.contact')}</button></li>
              <li><Link to="/careers" className={linkCls} data-testid="footer-careers">{u('careers')}</Link></li>
            </Col>
          </div>

          <div className="lg:col-span-2">
            <Col title={t('footer.legal')}>
              <li><Link to="/privacy-policy" className={linkCls} data-testid="footer-privacy">{t('footer.privacy')}</Link></li>
              <li><Link to="/terms-of-service" className={linkCls} data-testid="footer-terms">{t('footer.terms')}</Link></li>
              <li><Link to="/aml-policy" className={linkCls} data-testid="footer-aml">{t('footer.aml')}</Link></li>
            </Col>
          </div>
        </div>

        <div className="mt-20 flex flex-wrap gap-2" aria-label="Languages">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`font-mono-lux text-[0.7rem] tracking-[0.12em] min-w-[44px] min-h-[44px] px-3.5 rounded-full border transition-colors duration-300 ${language === lang.code ? 'border-[#C8AA73] text-[#E8D6AF]' : 'border-[#F4F0E8]/10 text-[#7C776E] hover:text-[#F4F0E8] hover:border-[#F4F0E8]/25'}`}
              data-testid={`footer-lang-${lang.code}`}
            >
              {lang.code.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="mt-16 select-none pointer-events-none font-display font-light leading-[0.8] text-[clamp(4rem,17vw,17rem)] tracking-[-0.04em] text-transparent" style={{ WebkitTextStroke: '1px rgba(200,170,115,0.18)' }} aria-hidden="true">
          SoftMaya
        </div>

        <div className="mt-10 pt-8 border-t border-[#F4F0E8]/[0.07] flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-[#7C776E] text-sm" data-testid="footer-copyright">{t('footer.copyright')}</p>
          <p className="flex items-center gap-3" data-testid="footer-powered-by">
            <span className="text-xs text-[#7C776E]">Powered by</span>
            <span className="font-mono-lux text-[0.72rem] tracking-[0.16em] uppercase text-[#C8AA73]">SoftMaya</span>
          </p>
        </div>
      </div>
    </footer>
  );
};
